/**
 * Tüm aktif personeller için anlık maliyet hesabı.
 *
 * Ana metrik: "Anlık biriken maliyet" = çalışılan saat × saatlik karşılık
 *   - Aylık personel için: aylık ücret ÷ 300 = saatlik karşılık
 *   - Saatlik personel için: zaten saatlik ücret
 *   - Her ikisi için anlamlı: BOSS açısından "şu ana kadar bana ne kadar mal oldu"
 *
 * Mesaide olan herkes için canlı sayaç akar (aylık dahil).
 */

import { collection, getDocs, query, where } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { maasHesapla, saatlikKarsilik, type MaasHesabi } from './maas-hesap'
import type { KullaniciPersonel } from '@/types'
import type { Kayit } from '@/lib/services/kayit-service'

export interface PersonelMaliyetSatiri {
  personel: KullaniciPersonel
  hesap: MaasHesabi
  /** Çalışılan saat × saatlik + izin alacağı (build-up aylık izin hakkı + yıllık izin). */
  anlikMaliyet: number
  /** Saatlik karşılık (referans). */
  saatlik: number
  /** Gerçek ay sonu tahmini (aylık: maaş bazlı; saatlik: şu anki net). */
  tahminiNet: number
}

export interface ToplamMaliyetOzeti {
  donem: string
  personelSayisi: number

  /** Anlık biriken toplam maliyet (saat × saatlik). */
  toplamAnlikMaliyet: number

  /** Şu ana kadarki net toplam (gerçek hesap). */
  toplamNetMaas: number

  /** Gerçek ay sonu tahmini toplam (aylık: maaş bazlı). */
  toplamTahminiNet: number

  toplamCalismaSaat: number
  toplamPuantaj: number
  toplamOdenmisAvans: number
  satirlar: PersonelMaliyetSatiri[]
}

export interface AktifMesaiBilgisi {
  personelId: string
  saatlik: number
}

export interface AktifMaliyetOzeti {
  /** Mesaidekilerin saatlik karşılık toplamı (her saniye akan birikim için). */
  saatlikToplam: number
  aktifPersonelSayisi: number
  detay: AktifMesaiBilgisi[]
}

/**
 * Gerçek ay sonu tahmini (maaş bazlı — kullanıcı kararı A).
 * Aylık personel normal bir ay çalışırsa net = aylık maaş (izin/eksik kalibreli).
 * Üzerine o ayki gerçek ek/kesinti kalemleri eklenir; geleceğe ait varsayım yapılmaz.
 * Saatlik personelde maaş bazı yoktur → şu anki net döndürülür.
 */
function tahminiNetMaas(p: KullaniciPersonel, h: MaasHesabi): number {
  if (p.maasTipi !== 'aylik') return h.netMaas
  return p.ucret
    + h.tatilBonusToplam
    + h.dogumGunuBonus
    + h.puantajToplam
    - h.odenmisAvansToplam
    + h.sabitEkOdeme
    - h.sabitKesinti
    + h.manuelHareketNet
    - h.molaAsanKesinti
    - h.gecGelmeCezasi
    - h.noShowCezasi
}

export async function tumAktifPersonellerinMaliyeti(
  donem: string,
  opts: { ayrilanlariDahilEt?: boolean } = {}
): Promise<ToplamMaliyetOzeti> {
  const tumPersoneller = await tumPersonelleriGetir()
  const aktifler = tumPersoneller.filter((p) => p.aktif)
  // Ayrılan (pasif) ama bu dönemde etkinliği olan personeli de dahil et (maaş ödemesi için).
  const pasifAdaylar = opts.ayrilanlariDahilEt
    ? tumPersoneller.filter((p) => !p.aktif)
    : []

  const adaylar = [...aktifler, ...pasifAdaylar]
  const hesaplar = await Promise.all(
    adaylar.map((p) =>
      maasHesapla(p, donem).catch((e) => {
        console.warn(`[maliyet] "${p.isim}" maaş hesaplanamadı, atlandı:`, e)
        return null
      })
    )
  )

  // Aktifler her zaman; pasifler yalnız bu dönemde etkinlik varsa (çalışma/puantaj/avans/manuel).
  const secili: { p: KullaniciPersonel; h: MaasHesabi }[] = []
  adaylar.forEach((p, i) => {
    const h = hesaplar[i]
    if (!h) return
    if (p.aktif) {
      secili.push({ p, h })
      return
    }
    const etkin =
      h.toplamSaat > 0 ||
      h.puantajToplam !== 0 ||
      h.odenmisAvansToplam > 0 ||
      h.manuelHareketNet !== 0
    if (etkin) secili.push({ p, h })
  })

  const satirlar: PersonelMaliyetSatiri[] = secili.map(({ p, h }) => {
    const s = saatlikKarsilik(p)
    return {
      personel: p,
      hesap: h,
      saatlik: s,
      anlikMaliyet: h.toplamSaat * s + h.izinAlacagi + h.yillikIzinOdemesi,
      tahminiNet: tahminiNetMaas(p, h),
    }
  })

  let toplamAnlik = 0
  let toplamNet = 0
  let toplamTahmini = 0
  let toplamSaat = 0
  let toplamPuantaj = 0
  let toplamAvans = 0

  for (const s of satirlar) {
    toplamAnlik += s.anlikMaliyet
    toplamNet += s.hesap.netMaas
    toplamTahmini += s.tahminiNet
    toplamSaat += s.hesap.toplamSaat
    toplamPuantaj += s.hesap.puantajToplam
    toplamAvans += s.hesap.odenmisAvansToplam
  }

  return {
    donem,
    personelSayisi: satirlar.length,
    toplamAnlikMaliyet: toplamAnlik,
    toplamNetMaas: toplamNet,
    toplamTahminiNet: toplamTahmini,
    toplamCalismaSaat: toplamSaat,
    toplamPuantaj,
    toplamOdenmisAvans: toplamAvans,
    satirlar: satirlar.sort((a, b) => b.anlikMaliyet - a.anlikMaliyet),
  }
}

export async function aktifMesaiMaliyeti(): Promise<AktifMaliyetOzeti> {
  const db = getFirestoreDb()
  const tumPersoneller = await tumPersonelleriGetir()
  const aktifler = tumPersoneller.filter((p) => p.aktif)

  const sonuclar = await Promise.all(
    aktifler.map(async (p) => {
      const q = query(collection(db, 'kayitlar'), where('personelId', '==', p.id))
      const snap = await getDocs(q)
      const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
      const sonKayit = liste.sort((a, b) => (b.zaman?.toMillis?.() ?? 0) - (a.zaman?.toMillis?.() ?? 0))[0]
      return { p, son: sonKayit }
    })
  )

  const mesaidekiler = sonuclar.filter((x) => x.son?.tip === 'giris')

  let saatlikToplam = 0
  const detay: AktifMesaiBilgisi[] = []

  for (const x of mesaidekiler) {
    const s = saatlikKarsilik(x.p)
    saatlikToplam += s
    detay.push({ personelId: x.p.id, saatlik: s })
  }

  return {
    aktifPersonelSayisi: mesaidekiler.length,
    saatlikToplam,
    detay,
  }
}
